import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

interface Doc {
  slug: string;
  title: string;
  category: string;
  sensitivity: 'public' | 'internal' | 'personal';
  content: string;
  source?: string | null;
  updated_at?: string;
}

type Draft = Pick<Doc, 'slug' | 'title' | 'category' | 'sensitivity' | 'content'> & { isNew: boolean };

const BLANK: Draft = { slug: '', title: '', category: 'business', sensitivity: 'internal', content: '', isNew: true };
const SENS_NOTE: Record<string, string> = {
  public: 'Public — non-sensitive, safe to expose anywhere.',
  internal: 'Internal — business knowledge (rates, methods, examples). Used by the assistant.',
  personal: 'Personal — contains personal data. Never committed to git; lives only in the DB.',
};

export function KnowledgeBase() {
  const qc = useQueryClient();
  const { data: docs = [], isLoading } = useQuery({ queryKey: ['context-docs'], queryFn: () => api.getContextDocuments() as Promise<Doc[]> });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = useMutation({
    mutationFn: (d: Draft) => api.saveContextDocument({ slug: d.slug, title: d.title, category: d.category, sensitivity: d.sensitivity, content: d.content }),
    onSuccess: (saved: any) => {
      qc.invalidateQueries({ queryKey: ['context-docs'] });
      setDraft({ ...(saved as Doc), isNew: false });
    },
  });
  const del = useMutation({
    mutationFn: (slug: string) => api.deleteContextDocument(slug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['context-docs'] });
      setDraft(null);
      setConfirmDelete(false);
    },
  });

  const open = (d: Doc) => { setConfirmDelete(false); setDraft({ slug: d.slug, title: d.title, category: d.category, sensitivity: d.sensitivity, content: d.content, isNew: false }); };
  const newDoc = () => { setConfirmDelete(false); setDraft({ ...BLANK }); };
  const upd = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const input: React.CSSProperties = { width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--color-border,#ccc)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' };
  const label: React.CSSProperties = { display: 'block', fontWeight: 600, fontSize: 13, margin: '12px 0 4px' };

  return (
    <div style={{ display: 'flex', gap: 16, padding: 'var(--spacing-3,16px)', height: '100%', boxSizing: 'border-box' }}>
      {/* List */}
      <div style={{ width: 280, flexShrink: 0, overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Knowledge base</h3>
          <button onClick={newDoc} style={{ background: 'var(--color-teal,#00B097)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontWeight: 600 }}>+ New</button>
        </div>
        {isLoading && <p>Loading…</p>}
        {docs.map((d) => (
          <button key={d.slug} onClick={() => open(d)}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', marginBottom: 4, borderRadius: 6, cursor: 'pointer',
              border: '1px solid var(--color-border,#e0e0e0)', background: draft && !draft.isNew && draft.slug === d.slug ? 'var(--color-teal-25,#D6EBE5)' : 'var(--color-surface,#fff)' }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{d.title}</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted,#666)' }}>{d.category} · {d.sensitivity}</div>
          </button>
        ))}
      </div>

      {/* Editor */}
      <div style={{ flex: 1, overflowY: 'auto', borderLeft: '1px solid var(--color-border,#e0e0e0)', paddingLeft: 16 }}>
        {!draft && <p style={{ color: 'var(--color-text-muted,#666)' }}>Select a document to edit, or create a new one. Changes apply immediately — the assistant reads these live.</p>}
        {draft && (
          <div style={{ maxWidth: 720 }}>
            <label style={label}>Title</label>
            <input style={input} value={draft.title} onChange={(e) => upd({ title: e.target.value })} placeholder="e.g. Reply & Quoting Examples" />

            <label style={label}>Slug (id)</label>
            <input style={{ ...input, opacity: draft.isNew ? 1 : 0.6 }} value={draft.slug} disabled={!draft.isNew}
              onChange={(e) => upd({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-') })} placeholder="reply-examples" />
            {!draft.isNew && <div style={{ fontSize: 11, color: 'var(--color-text-muted,#666)', marginTop: 2 }}>Slug can't change on an existing doc.</div>}

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={label}>Category</label>
                <input style={input} value={draft.category} onChange={(e) => upd({ category: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>Sensitivity</label>
                <select style={input} value={draft.sensitivity} onChange={(e) => upd({ sensitivity: e.target.value as Draft['sensitivity'] })}>
                  <option value="public">public</option>
                  <option value="internal">internal</option>
                  <option value="personal">personal</option>
                </select>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted,#666)', marginTop: 4 }}>{SENS_NOTE[draft.sensitivity]}</div>

            <label style={label}>Content (Markdown)</label>
            <textarea style={{ ...input, minHeight: 320, fontFamily: 'ui-monospace, monospace', fontSize: 13 }} value={draft.content} onChange={(e) => upd({ content: e.target.value })} />

            <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
              <button onClick={() => draft.slug && draft.title && save.mutate(draft)} disabled={!draft.slug || !draft.title || save.isPending}
                style={{ background: 'var(--color-teal,#00B097)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 600, cursor: 'pointer', opacity: (!draft.slug || !draft.title) ? 0.6 : 1 }}>
                {save.isPending ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </button>
              {!draft.isNew && (
                confirmDelete ? (
                  <>
                    <span style={{ fontSize: 13, color: 'var(--color-error,#EE2937)' }}>Delete “{draft.title}”?</span>
                    <button onClick={() => del.mutate(draft.slug)} disabled={del.isPending} style={{ background: 'var(--color-error,#EE2937)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>Yes, delete</button>
                    <button onClick={() => setConfirmDelete(false)} style={{ background: 'none', border: '1px solid var(--color-border,#ccc)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
                  </>
                ) : (
                  <button onClick={() => setConfirmDelete(true)} style={{ background: 'none', border: '1px solid var(--color-error,#EE2937)', color: 'var(--color-error,#EE2937)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>Delete</button>
                )
              )}
              {save.isError && <span style={{ color: 'var(--color-error,#EE2937)', fontSize: 13 }}>Save failed.</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
